/**
 * Page content normaliser.
 *
 * Page bodies are authored in the Shopify admin, so the markup that reaches the
 * theme carries editor leftovers: spacer paragraphs, per-paragraph alignment and
 * colours, and video embeds with hard-coded width/height attributes.
 *
 * This pass rewrites those into structures the stylesheet can lay out — without
 * touching the copy itself. It is progressive enhancement: with JS disabled the
 * CSS still produces a single readable column and responsive embeds.
 */
(() => {
  const INLINE_STYLES_TO_DROP = [
    'text-align',
    'font-family',
    'font-size',
    'line-height',
    'color',
    'background-color',
    'margin-left',
    'margin-right',
    'padding-left',
    'padding-right',
    'width',
    'height',
  ];

  const EMBED_SELECTOR = 'iframe, video, embed, object';
  const SHORT_LINE_LENGTH = 45;
  const LEAD_IN_LENGTH = 64;
  const NUMBERED_PATTERN = /^\s*(\d+|[A-Za-z])[).]\s+/;

  const text = (el) => (el.textContent || '').replace(/ /g, ' ').trim();

  const isBlank = (el) => text(el) === '' && !el.querySelector(`img, ${EMBED_SELECTOR}`);

  const isFullyBold = (el) => {
    const strong = el.querySelector('strong, b');
    return Boolean(strong) && text(strong) === text(el) && text(el) !== '';
  };

  /**
   * Anything carrying a class was written deliberately — a hand-built layout in
   * the page body, or a decorative element. The normaliser only touches the
   * unstyled soup the rich-text editor produces.
   */
  const isAuthored = (el) => el.classList.length > 0;

  /** Strip editor styling so the stylesheet owns typography and alignment. */
  const stripInlineStyles = (root) => {
    root.querySelectorAll('[style]').forEach((el) => {
      if (isAuthored(el)) return;
      INLINE_STYLES_TO_DROP.forEach((prop) => el.style.removeProperty(prop));
      if (el.getAttribute('style') === '') el.removeAttribute('style');
    });
    root.querySelectorAll('[align]').forEach((el) => el.removeAttribute('align'));
  };

  /** Remove spacer paragraphs — the source of the erratic gaps. */
  const removeBlankNodes = (root) => {
    root.querySelectorAll('p, div, span, h1, h2, h3, h4, h5, h6').forEach((el) => {
      if (!isAuthored(el) && isBlank(el)) el.remove();
    });
  };

  /**
   * Flatten the editor's grouping divs. The layout is a grid, so rhythm and the
   * full-width media lane only reach direct children.
   */
  const unwrapContainers = (root) => {
    for (let pass = 0; pass < 5; pass += 1) {
      const wrappers = [...root.children].filter(
        (el) => el.tagName === 'DIV' && !el.classList.length && el.children.length > 0
      );
      if (!wrappers.length) return;
      wrappers.forEach((wrapper) => wrapper.replaceWith(...wrapper.childNodes));
    }
  };

  /**
   * Lift every embed to the top level of the content grid, wrapped in a 16:9
   * frame, so it can span the full container width.
   */
  const promoteEmbeds = (root) => {
    root.querySelectorAll(EMBED_SELECTOR).forEach((embed) => {
      if (embed.closest('.page-content__media')) return;

      // Hard-coded embed dimensions never survive a responsive layout, so drop
      // them whether or not the surrounding markup was authored.
      ['width', 'height', 'frameborder'].forEach((attr) => embed.removeAttribute(attr));

      if (embed.tagName === 'IFRAME') {
        embed.setAttribute('loading', 'lazy');
        embed.setAttribute('allowfullscreen', '');
        if (!embed.getAttribute('title')) embed.setAttribute('title', 'Video');
      }

      // Authored markup frames its own media — only rescue bare embeds.
      const parent = embed.parentElement;
      if (!parent || isAuthored(embed) || (parent !== root && isAuthored(parent))) return;

      embed.removeAttribute('style');

      // Walk up to the outermost wrapper that exists only to hold this embed.
      let node = embed;
      while (
        node.parentElement &&
        node.parentElement !== root &&
        !isAuthored(node.parentElement) &&
        text(node.parentElement) === text(embed) &&
        node.parentElement.querySelectorAll(EMBED_SELECTOR).length === 1 &&
        !node.parentElement.querySelector('img')
      ) {
        node = node.parentElement;
      }

      const frame = document.createElement('div');
      frame.className = 'page-content__media';

      node.parentElement.insertBefore(frame, node);
      frame.appendChild(embed);

      // Drop the emptied wrapper, keep it if it still carries copy.
      if (node !== embed && isBlank(node)) node.remove();
    });
  };

  /**
   * Give flat paragraph soup a visual hierarchy: section labels, numbered
   * items, one-line facts and standalone calls to action.
   */
  const classifyParagraphs = (root) => {
    root.querySelectorAll('p').forEach((p) => {
      if (isAuthored(p)) return;

      const content = text(p);
      if (!content) return;

      const bold = isFullyBold(p);
      const numbered = NUMBERED_PATTERN.test(content);

      // "Pricing Options:" and friends open a section — promote them.
      if (!bold && !numbered && content.length <= LEAD_IN_LENGTH && content.endsWith(':')) {
        p.classList.add('page-content__lead-in');
        return;
      }

      // A bold, numbered line heads a group of details below it.
      if (bold && numbered) {
        p.classList.add('page-content__lead-in', 'page-content__numbered');
        return;
      }

      if (numbered) p.classList.add('page-content__numbered');
      if (content.length <= SHORT_LINE_LENGTH) p.classList.add('page-content__short');
    });
  };

  /** A paragraph holding nothing but a link is an action, so render it as one. */
  const promoteCallsToAction = (root) => {
    root.querySelectorAll('p').forEach((p) => {
      if (isAuthored(p)) return;

      const link = p.querySelector('a');
      if (!link || p.querySelectorAll('a').length !== 1) return;
      if (isAuthored(link)) return;
      if (text(link) !== text(p) || text(link).length < 4) return;

      const href = link.getAttribute('href') || '';
      if (/^(mailto:|tel:)/i.test(href)) return;

      // The button styles colour and layer .btn__text, so the label needs it.
      if (!link.querySelector('.btn__text')) {
        const label = document.createElement('span');
        label.className = 'btn__text';
        label.append(...link.childNodes);
        link.appendChild(label);
      }

      link.classList.add('btn', 'btn--primary');
      p.classList.add('page-content__cta');
    });
  };

  const normalize = (root) => {
    if (!root || root.dataset.normalized === 'true') return;
    root.dataset.normalized = 'true';

    stripInlineStyles(root);
    removeBlankNodes(root);
    unwrapContainers(root);
    promoteEmbeds(root);
    // Order matters: classifyParagraphs tags elements, which makes them look
    // authored to every step that runs after it.
    promoteCallsToAction(root);
    classifyParagraphs(root);
  };

  const normalizeAll = (scope = document) =>
    scope.querySelectorAll('[data-page-content-normalize]').forEach(normalize);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => normalizeAll());
  } else {
    normalizeAll();
  }

  // Theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', (event) => normalizeAll(event.target));
})();
